/*
 * Redolia smart search.
 *
 * Opens in place of the theme's stock search pop-up. Everything a shopper types is answered in the
 * browser, from one small index of the catalogue (assets/rdl-search-index.json): no round trip per
 * keystroke, so results change as fast as the keys do.
 *
 * What "smart" means here:
 *   - spelling does not have to be right ("sospro", "vanila", "bacarat");
 *   - it reads a request, not only a name: "sweet vanilla for winter under $200 for her" becomes
 *     notes + a season + a price ceiling + a gender, and "like Il Padrino" ranks by shared notes;
 *   - when the words are a description the index cannot settle ("smells like a campfire",
 *     "like Baccarat Rouge"), the Redolia advisor (the same AI as the perfume finder) is asked and its
 *     picks arrive a second later, above the instant results.
 *
 * If this file or the index fails to load, the theme's own search still opens: the click is only taken
 * over once the dialog below exists.
 */
(() => {
  const CFG = Object.assign(
    {
      index: '',
      ai: '',
      searchUrl: '/search',
      popular: ['Vanilla', 'Oud', 'Fresh citrus', 'Rose', 'For him', 'For her', 'Under $150', 'Date night', 'New arrivals'],
      ask: ['sweet vanilla for winter under $200', 'fresh office scent for him', 'something like Baccarat Rouge'],
    },
    window.RDL_SEARCH || {}
  );
  const dialog = document.getElementById('rdl-search');
  if (!dialog || typeof dialog.showModal !== 'function') return;
  // Until CFG.enabled is true the panel is only for whoever opened the site once with ?rdl_search=1
  // (and ?rdl_search=0 switches that browser back to the stock search).
  let previewing = false;
  try {
    if (/[?&]rdl_search=1\b/.test(location.search)) localStorage.setItem('rdl-search-preview', '1');
    if (/[?&]rdl_search=0\b/.test(location.search)) localStorage.removeItem('rdl-search-preview');
    previewing = localStorage.getItem('rdl-search-preview') === '1';
  } catch (e) { previewing = /[?&]rdl_search=1\b/.test(location.search); }
  if (!CFG.enabled && !previewing) return;

  const $ = (sel) => dialog.querySelector(sel);
  const input = $('.rdls-input');
  const elClear = $('.rdls-clear');
  const elUnderstood = $('.rdls-understood');
  const elSide = $('.rdls-side');
  const elMain = $('.rdls-main');
  const elStatus = $('.rdls-status');

  /* ------------------------------------------------------------------ text helpers */
  const norm = (s) =>
    String(s == null ? '' : s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[’'`´]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (n) => '$' + Number(n).toLocaleString('en-US');
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many || one + 's'}`;

  /** Edit distance with a ceiling: stops as soon as the words are further apart than `max`. */
  function distance(a, b, max) {
    const la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    let prev2 = null, prev = new Array(lb + 1), cur = new Array(lb + 1);
    for (let j = 0; j <= lb; j++) prev[j] = j;
    for (let i = 1; i <= la; i++) {
      cur[0] = i;
      let rowMin = cur[0];
      for (let j = 1; j <= lb; j++) {
        const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charCodeAt(i - 1) === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) v = Math.min(v, prev2[j - 2] + 1);
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev; prev = cur; cur = new Array(lb + 1);
    }
    return prev[lb];
  }

  /** How well a typed word `q` stands for a catalogue word `c`: 1 exact … 0 not at all. */
  function quality(q, c, typing) {
    if (q === c) return 1;
    const lq = q.length, lc = c.length;
    if (lq >= 2 && lc > lq && c.startsWith(q)) return typing ? 0.9 : lq >= 3 ? 0.8 : 0.55;
    if (lq >= 4 && q.charCodeAt(0) === c.charCodeAt(0)) {
      const max = lq >= 8 ? 2 : 1;
      const d = distance(q, c, max);
      if (d <= max) return d === 1 ? 0.72 : 0.55;
    }
    if (lq >= 5 && Math.abs(lq - lc) <= 1 && distance(q, c, 1) <= 1) return 0.6; // first letter wrong
    if (lq >= 4 && lc > lq && c.includes(q)) return 0.45;
    return 0;
  }

  /* ------------------------------------------------------------------ what words mean */
  const STOP = new Set(
    'a an the and or with for of in on to that this is it i im me my we you your want wants looking look need needs something anything some any good nice great best perfume perfumes fragrance fragrances cologne colognes scent scents smell smells smelling parfum eau de edp edt extrait spray bottle please show find get buy can do does like similar'.split(' ')
  );
  const GENERIC = new Set('eau de parfum extrait edp edt spray oz ml fl the and by intense elixir cologne toilette'.split(' '));

  /** A mood or style word → the accords and notes that carry it. */
  const CONCEPTS = {
    sweet: { acc: ['sweet', 'gourmand', 'vanilla', 'caramel', 'honey'], notes: ['vanilla', 'caramel', 'praline', 'honey', 'sugar', 'tonka', 'toffee', 'marshmallow'] },
    gourmand: { acc: ['gourmand', 'sweet', 'vanilla', 'chocolate', 'caramel', 'nutty', 'coffee'], notes: ['vanilla', 'caramel', 'chocolate', 'praline', 'coffee', 'pistachio', 'almond'] },
    fresh: { acc: ['fresh', 'citrus', 'aquatic', 'green', 'fresh spicy', 'aromatic', 'ozonic', 'marine'], notes: ['bergamot', 'lemon', 'grapefruit', 'mint', 'neroli', 'sea', 'marine', 'green tea'] },
    clean: { acc: ['fresh', 'soapy', 'musky', 'white floral', 'aldehydic', 'powdery', 'ozonic'], notes: ['musk', 'white musk', 'aldehydes', 'cotton', 'soap', 'lavender', 'neroli'] },
    citrus: { acc: ['citrus', 'fresh'], notes: ['bergamot', 'lemon', 'grapefruit', 'orange', 'mandarin', 'lime', 'yuzu', 'neroli', 'petitgrain'] },
    citrusy: { same: 'citrus' },
    woody: { acc: ['woody', 'oud', 'earthy', 'smoky'], notes: ['sandalwood', 'cedar', 'vetiver', 'oud', 'guaiac', 'patchouli', 'cypress', 'pine'] },
    wood: { same: 'woody' },
    smoky: { acc: ['smoky', 'leather', 'tobacco', 'incense', 'woody'], notes: ['incense', 'birch', 'smoke', 'tobacco', 'leather', 'cade', 'guaiac', 'olibanum'] },
    smokey: { same: 'smoky' },
    spicy: { acc: ['warm spicy', 'fresh spicy', 'spicy', 'cinnamon'], notes: ['pepper', 'cardamom', 'cinnamon', 'saffron', 'clove', 'nutmeg', 'ginger', 'pink pepper'] },
    warm: { acc: ['amber', 'warm spicy', 'vanilla', 'balsamic', 'sweet'], notes: ['amber', 'vanilla', 'benzoin', 'tonka', 'labdanum', 'cinnamon'] },
    cozy: { same: 'warm' },
    fruity: { acc: ['fruity', 'tropical', 'sweet'], notes: ['peach', 'apple', 'pineapple', 'blackcurrant', 'pear', 'plum', 'raspberry', 'mango', 'cherry', 'lychee', 'strawberry'] },
    fruit: { same: 'fruity' },
    floral: { acc: ['floral', 'white floral', 'rose', 'yellow floral'], notes: ['rose', 'jasmine', 'tuberose', 'iris', 'orange blossom', 'ylang', 'peony', 'lily', 'violet'] },
    flowery: { same: 'floral' },
    flowers: { same: 'floral' },
    powdery: { acc: ['powdery', 'iris', 'violet', 'musky'], notes: ['iris', 'orris', 'violet', 'heliotrope', 'musk', 'powder'] },
    musky: { acc: ['musky', 'powdery', 'animalic'], notes: ['musk', 'white musk', 'ambrette', 'cashmeran'] },
    boozy: { acc: ['boozy', 'rum', 'whiskey', 'warm spicy'], notes: ['rum', 'cognac', 'whisky', 'whiskey', 'wine', 'champagne', 'bourbon'] },
    leathery: { acc: ['leather', 'animalic', 'smoky'], notes: ['leather', 'suede', 'birch'] },
    green: { acc: ['green', 'aromatic', 'herbal', 'fresh'], notes: ['galbanum', 'fig leaf', 'grass', 'basil', 'mint', 'green tea', 'violet leaf'] },
    aquatic: { acc: ['aquatic', 'marine', 'ozonic', 'fresh'], notes: ['sea', 'marine', 'salt', 'seaweed', 'water', 'calone'] },
    ocean: { same: 'aquatic' },
    creamy: { acc: ['lactonic', 'creamy', 'vanilla', 'coconut', 'milky'], notes: ['milk', 'sandalwood', 'coconut', 'vanilla', 'cream', 'tonka'] },
    earthy: { acc: ['earthy', 'woody', 'mossy', 'patchouli'], notes: ['patchouli', 'vetiver', 'oakmoss', 'moss', 'soil'] },
    dark: { acc: ['oud', 'smoky', 'leather', 'amber', 'balsamic'], notes: ['oud', 'incense', 'patchouli', 'leather', 'tobacco', 'labdanum'] },
    tropical: { acc: ['tropical', 'coconut', 'fruity'], notes: ['coconut', 'mango', 'pineapple', 'tiare', 'passion fruit', 'banana'] },
    beach: { acc: ['tropical', 'coconut', 'aquatic', 'marine', 'citrus'], notes: ['coconut', 'sea', 'salt', 'tiare', 'monoi', 'solar'], season: 1 },
    sexy: { acc: ['amber', 'musky', 'vanilla', 'animalic', 'warm spicy'], notes: ['amber', 'musk', 'vanilla', 'tonka', 'oud'], night: true },
    seductive: { same: 'sexy' },
    elegant: { acc: ['iris', 'powdery', 'floral', 'woody', 'aldehydic'], notes: ['iris', 'rose', 'sandalwood', 'jasmine', 'musk'] },
    classy: { same: 'elegant' },
    unique: { acc: ['smoky', 'animalic', 'leather', 'oud', 'green', 'metallic'], notes: ['incense', 'oud', 'leather', 'saffron'] },
    barbershop: { acc: ['aromatic', 'lavender', 'fougere', 'fresh spicy', 'powdery'], notes: ['lavender', 'oakmoss', 'geranium', 'tonka', 'coumarin'] },
    // everyday words for the same moods
    campfire: { same: 'smoky' }, bonfire: { same: 'smoky' }, fireplace: { same: 'smoky' }, smoke: { same: 'smoky' }, burnt: { same: 'smoky' },
    laundry: { same: 'clean' }, soap: { same: 'clean' }, soapy: { same: 'clean' }, shower: { same: 'clean' },
    dessert: { same: 'gourmand' }, candy: { same: 'gourmand' }, sugary: { same: 'gourmand' }, bakery: { same: 'gourmand' }, cake: { same: 'gourmand' }, edible: { same: 'gourmand' },
    forest: { same: 'woody' }, woods: { same: 'woody' },
    sea: { same: 'aquatic' }, rain: { same: 'aquatic' }, marine: { same: 'aquatic' },
    cosy: { same: 'warm' }, comforting: { same: 'warm' },
    sensual: { same: 'sexy' }, sophisticated: { same: 'elegant' },
    garden: { same: 'floral' }, bouquet: { same: 'floral' },
    cocktail: { same: 'boozy' }, gym: { same: 'fresh' }, sporty: { same: 'fresh' },
  };
  /** Words that are not a product word but a filter. */
  const GENDER = {
    men: 'Men', mens: 'Men', man: 'Men', male: 'Men', masculine: 'Men', him: 'Men', his: 'Men', husband: 'Men', boyfriend: 'Men', dad: 'Men', father: 'Men', guy: 'Men', guys: 'Men', gentleman: 'Men', homme: 'Men',
    women: 'Women', womens: 'Women', woman: 'Women', female: 'Women', feminine: 'Women', her: 'Women', hers: 'Women', wife: 'Women', girlfriend: 'Women', mom: 'Women', mother: 'Women', lady: 'Women', ladies: 'Women', girl: 'Women', femme: 'Women',
    unisex: 'Unisex',
  };
  const SEASON = { spring: 0, summer: 1, hot: 1, heat: 1, vacation: 1, holiday: 1, fall: 2, autumn: 2, winter: 3, cold: 3, christmas: 3, snow: 3 };
  const SEASON_NAME = ['Spring', 'Summer', 'Fall', 'Winter'];
  /** Occasions the catalogue is tagged with: [the words, the tag, evening or daytime, the mood that goes with it]. */
  const OCCASIONS = [
    [/\b(?:date nights?|first dates?|(?:for|on) (?:a |the |my )?dates?)\b/g, 'Date Night', 'night', 'sexy'],
    [/\b(?:romantic|romance|valentines?(?: day)?|anniversary)\b/g, 'Romantic Evening', 'night', 'sexy'],
    [/\b(?:night out|nights out|evening out|going out|clubbing|club|party|parties)\b/g, 'Evening Out', 'night', 'sexy'],
    [/\bdinners?\b/g, 'Dinner', 'night', 'warm'],
    [/\b(?:office|work|workplace|professional|interviews?|school)\b/g, 'Office', 'day', 'clean'],
    [/\b(?:everyday|every day|daily|casual)\b/g, 'Everyday', 'day', ''],
    [/\b(?:special occasions?|weddings?|formal|gala|black tie)\b/g, 'Special Occasions', '', 'elegant'],
  ];
  const NIGHT = new Set(['night', 'evening', 'date', 'club', 'clubbing', 'party', 'dinner', 'romantic']);
  const DAY = new Set(['day', 'daytime', 'office', 'work', 'school', 'gym', 'daily', 'everyday', 'casual', 'brunch']);

  /* ------------------------------------------------------------------ the index */
  let INDEX = null, loading = null, loadedAt = 0, refreshing = null;
  /* The index address in the page is fixed when the page is made. A page left open for hours, or one the phone brings
     back from its saved copy, would keep an old index: old prices, and pictures that have since been replaced. So when
     the panel opens with an index older than ten minutes, the shop is asked for the current address (a tiny section,
     sections/rdl-search-meta.liquid, rendered fresh), and if it changed the new index is loaded and the panel redrawn.
     (Asking for the old address with another query does not work: the file server keeps answering with the old file.) */
  const STALE_MS = 10 * 60 * 1000;
  function refreshIfStale() {
    if (!INDEX || !loadedAt || refreshing || Date.now() - loadedAt < STALE_MS) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    refreshing = fetch(root + '?section_id=rdl-search-meta', { credentials: 'omit', cache: 'no-store' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        loadedAt = Date.now();
        const m = /data-index="([^"]+)"/.exec(html || '');
        const url = m ? m[1].replace(/&amp;/g, '&') : '';
        if (!url || url === CFG.index) return null;
        return fetch(url, { credentials: 'omit' }).then((r) => (r.ok ? r.json() : null)).then((ix) => {
          if (!ix || !ix.p || !ix.p.length) return;
          CFG.index = url;
          prepare(ix);
          try { if (dialog.open) render(input.value); } catch (e) {}
          document.dispatchEvent(new CustomEvent('rdl-search-refreshed'));
        });
      })
      .catch(() => {})
      .then(() => { refreshing = null; });
  }
  window.addEventListener('pageshow', (e) => { if (e.persisted) { loadedAt = loadedAt ? 1 : 0; refreshIfStale(); } });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshIfStale(); });
  const VOCAB = new Map(); // word → [[productIndex, fieldWeight], …]
  let ENTITIES = [];       // brands, notes, accords, perfumers, families: things with a page of their own
  let PRICE_LOW = 0, PRICE_HIGH = 0;

  const W = { name: 10, brand: 8, note: 5, accord: 4.5, family: 4, perfumer: 4, occasion: 2, size: 1.5, gender: 1 };

  function addWords(text, pi, weight) {
    for (const w of norm(text).split(' ')) {
      if (!w) continue;
      let list = VOCAB.get(w);
      if (!list) VOCAB.set(w, (list = []));
      list.push([pi, weight]);
    }
  }

  function prepare(ix) {
    INDEX = ix;
    ix.p.forEach((p, i) => {
      p.nb = norm(p.b);
      p.nt = norm(p.t);
      // The name without the house in front of it and without "Eau de Parfum" at the end.
      let name = p.nt.startsWith(p.nb + ' ') ? p.nt.slice(p.nb.length + 1) : p.nt;
      p.nn = name.split(' ').filter((w) => !GENERIC.has(w) && !/^\d/.test(w)).join(' ') || name;
      // not a bottle of perfume: shown, but after the perfumes unless it was asked for by name
      const side = p.nt.match(/\b(candle|hand cream|cream|lotion|soap|body|diffuser|discovery|set|kit|sample|samples|gift card)\b/g);
      p.side = side ? side.join(' ') : '';
      p.notes = [...p.top, ...p.mid, ...p.base];
      p.nnotes = p.notes.map(norm);
      p.accMap = new Map(p.acc.map(([n, pct]) => [norm(n), pct || 40]));
      addWords(p.nn, i, W.name);
      if (p.k) addWords(norm(p.k), i, W.name);   // the name of the line it belongs to ("Collection Studio")
      addWords(p.nb, i, W.brand);
      for (const n of p.nnotes) addWords(n, i, W.note);
      for (const [n] of p.acc) addWords(n, i, W.accord);
      addWords(p.fam, i, W.family);
      addWords(p.perf, i, W.perfumer);
      for (const o of p.occ) addWords(o, i, W.occasion);
      for (const s of p.sz) addWords(s.replace(/(\d)\s*(ml|oz)/gi, '$1$2'), i, W.size);
      addWords(p.g, i, W.gender);
    });
    const prices = ix.p.map((p) => p.p).filter(Boolean).sort((a, b) => a - b);
    PRICE_LOW = prices[Math.floor(prices.length * 0.3)] || 150;
    PRICE_HIGH = prices[Math.floor(prices.length * 0.75)] || 350;
    const ent = (rows, kind, label) => {
      const seen = new Map();
      for (const [name, handle, count] of rows || []) {
        const n = norm(name), had = seen.get(n);
        if (had) { had.count += count; if (!had.handle) had.handle = handle; }
        else seen.set(n, { kind, label, name, n, handle, count });
      }
      return [...seen.values()];
    };
    ENTITIES = [...ent(ix.brands, 'brand', 'House'), ...ent(ix.notes, 'note', 'Note'), ...ent(ix.accords, 'accord', 'Accord'), ...ent(ix.perfumers, 'perfumer', 'Perfumer'), ...ent(ix.families, 'family', 'Family')];
  }

  function load() {
    if (INDEX) return Promise.resolve(INDEX);
    if (!loading) {
      loading = fetch(CFG.index, { credentials: 'omit' })
        .then((r) => {
          if (!r.ok) throw new Error('index ' + r.status);
          return r.json();
        })
        .then((ix) => (prepare(ix), (loadedAt = Date.now()), ix))
        .catch((e) => {
          loading = null;
          throw e;
        });
    }
    return loading;
  }

  /* ------------------------------------------------------------------ reading the request */
  function parse(rawQuery) {
    let text = ' ' + String(rawQuery).toLowerCase().replace(/[’`´]/g, "'") + ' ';
    const out = { raw: rawQuery, min: 0, max: 0, gender: '', season: -1, night: false, day: false, lasting: false, newest: false, inStock: false, like: '', not: [], concepts: [], occasions: [], terms: [], chips: [] };
    const take = (re, fn) => {
      text = text.replace(re, (...m) => {
        const keep = fn(...m);
        return typeof keep === 'string' ? ` ${keep} ` : ' ';
      });
    };
    // money
    take(/(?:between|from)\s*\$?\s*(\d{2,4})\s*(?:and|to|-)\s*\$?\s*(\d{2,4})/g, (_, a, b) => { out.min = Math.min(+a, +b); out.max = Math.max(+a, +b); });
    take(/\$\s*(\d{2,4})\s*(?:-|to)\s*\$?\s*(\d{2,4})/g, (_, a, b) => { out.min = Math.min(+a, +b); out.max = Math.max(+a, +b); });
    take(/(?:under|below|less than|cheaper than|max(?:imum)?|up to|no more than|<)\s*\$?\s*(\d{2,4})(?:\s*(?:dollars|bucks|usd))?/g, (_, a) => { out.max = +a; });
    take(/(?:over|above|more than|at least|min(?:imum)?|>)\s*\$?\s*(\d{2,4})(?:\s*(?:dollars|bucks|usd))?/g, (_, a) => { out.min = +a; });
    take(/\$\s*(\d{2,4})\b/g, (_, a) => { if (!out.max) out.max = +a; });
    take(/\b(cheap|cheapest|affordable|budget|inexpensive)\b/g, () => { if (!out.max) { out.max = PRICE_LOW; out.cheap = true; } });
    take(/\b(luxury|luxurious|expensive|high end|premium)\b/g, () => { if (!out.min) { out.min = PRICE_HIGH; out.lux = true; } });
    // "like X"
    take(/\b(?:smells? like|similar to|something like|alternative to|dupe (?:for|of)|clone of|inspired by|in the style of|reminds me of|like)\s+(?!to\b)(.+?)\s*(?=$|\bbut\b|\bfor\b|\bunder\b|\bbelow\b|\bwith\b)/, (_, ref) => {
      // "smells like a campfire" is a mood, not a perfume to compare with
      const mood = norm(ref).split(' ').filter((w) => w && !/^(a|an|the|some|my|fresh|clean)$/.test(w));
      if (mood.length && mood.every((w) => CONCEPTS[w])) return norm(ref);
      out.like = ref.trim();
    });
    // "not sweet", "no vanilla", "without oud"
    take(/\b(?:not too|not|no|without|non|less)\s+([a-z]+)/g, (_, w) => { out.not.push(w); });
    take(/\b(long[\s-]?lasting|lasts? (?:long|all day)|all day|strong|powerful|beast ?mode|projection)\b/g, () => { out.lasting = true; });
    take(/\b(new arrivals?|newest|latest|just in|new in|new releases?|new)\b/g, () => { out.newest = true; });
    take(/\b(in stock|available)\b/g, () => { out.inStock = true; });
    // "for the office", "date night", "a wedding": bottles tagged for it come first; the words are not looked up in names
    for (const [re, tag, when, mood] of OCCASIONS) {
      take(re, () => {
        if (!out.occasions.some((o) => o.tag === tag)) out.occasions.push({ tag, mood });
        if (when === 'night') out.night = true;
        if (when === 'day') out.day = true;
      });
    }

    const words = norm(text).split(' ').filter(Boolean);
    for (let k = 0; k < words.length; k++) {
      const w = words[k];
      if (GENDER[w]) { out.gender = out.gender && out.gender !== GENDER[w] ? 'Unisex' : GENDER[w]; continue; }
      if (w in SEASON && !(w === 'fall' && words[k + 1] === 'in')) { out.season = SEASON[w]; continue; }
      if (NIGHT.has(w)) { out.night = true; if (w !== 'date' && w !== 'dinner' && w !== 'romantic' && w !== 'party') continue; }
      if (DAY.has(w)) { out.day = true; if (w === 'day' || w === 'daytime' || w === 'daily' || w === 'everyday' || w === 'casual') continue; }
      let c = CONCEPTS[w];
      if (c && c.same) c = CONCEPTS[c.same];
      if (c) { out.concepts.push({ word: w, ...c }); if (c.season != null && out.season < 0) out.season = c.season; if (c.night) out.night = true; }
      if (STOP.has(w)) continue;
      out.terms.push(w);
    }
    // what was understood, in the shopper's words
    if (out.like) out.chips.push('Like ' + out.like);
    for (const c of out.concepts) out.chips.push(c.word[0].toUpperCase() + c.word.slice(1));
    if (out.gender) out.chips.push(out.gender === 'Men' ? 'For him' : out.gender === 'Women' ? 'For her' : 'Unisex');
    if (out.season >= 0) out.chips.push(SEASON_NAME[out.season]);
    for (const o of out.occasions) out.chips.push(o.tag);
    if (out.night && !out.occasions.length) out.chips.push('Evening');
    if (out.day && !out.occasions.length) out.chips.push('Daytime');
    if (out.min && out.max) out.chips.push(`${money(out.min)}–${money(out.max)}`);
    else if (out.max) out.chips.push(out.cheap ? `Under ${money(out.max)}` : `Under ${money(out.max)}`);
    else if (out.min) out.chips.push(`Over ${money(out.min)}`);
    for (const n of out.not) out.chips.push('Not ' + n);
    if (out.lasting) out.chips.push('Long lasting');
    if (out.newest) out.chips.push('Newest first');
    return out;
  }

  /** The catalogue product a "like …" phrase points at, if we carry it. */
  function findReference(ref) {
    const words = norm(ref).split(' ').filter((w) => w && !GENERIC.has(w));
    if (!words.length) return null;
    let best = null, bestScore = 0;
    INDEX.p.forEach((p) => {
      const hay = (p.nb + ' ' + p.nn).split(' ');
      let s = 0;
      for (const w of words) {
        let q = 0;
        for (const h of hay) q = Math.max(q, quality(w, h, false));
        if (!q) return (s = -1);
        s += q;
      }
      if (s <= 0) return;
      s = s / words.length - Math.abs(p.nn.split(' ').length - words.length) * 0.02;
      if (s > bestScore) { bestScore = s; best = p; }
    });
    return bestScore >= 0.7 ? best : null;
  }

  function similarity(a, b) {
    if (a === b) return 0;
    const an = new Set(a.nnotes);
    let shared = 0;
    for (const n of b.nnotes) if (an.has(n)) shared++;
    const notes = shared / Math.max(6, Math.sqrt(a.nnotes.length * b.nnotes.length));
    let acc = 0;
    for (const [n, pct] of a.accMap) if (b.accMap.has(n)) acc += Math.min(pct, b.accMap.get(n)) / 100;
    const fam = a.fam && a.fam === b.fam ? 0.35 : a.fam && b.fam && (a.fam.includes(b.fam.split(' ')[0]) || b.fam.includes(a.fam.split(' ')[0])) ? 0.15 : 0;
    return notes * 2.2 + acc * 0.6 + fam;
  }

  /** Does this bottle fit the price, gender and stock limits of the request? */
  function within(q, p) {
    if ((q.max || q.min) && p.bo) return false;          // boutique-only: no price online
    if (q.max && p.p > q.max) return false;
    if (q.min && p.p < q.min) return false;
    if (q.gender === 'Men' && p.g === 'Women') return false;
    if (q.gender === 'Women' && p.g === 'Men') return false;
    if (q.gender === 'Unisex' && p.g !== 'Unisex') return false;
    if (q.inStock && !p.av) return false;
    return true;
  }

  /** What we carry that sits closest to these bottles (the advisor's picks), inside the request's limits. */
  function similarTo(picks, q, limit) {
    const skip = new Set(picks);
    const out = [];
    for (const p of INDEX.p) {
      if (skip.has(p) || p.side || (q && !within(q, p))) continue;
      let sum = 0, best = 0;
      for (const a of picks) { const v = similarity(a, p); sum += v; if (v > best) best = v; }
      let s = best * 0.6 + (sum / picks.length) * 0.4;
      if (s < 0.45) continue;
      if (!p.av) s *= 0.55;
      out.push({ p, s, words: null });
    }
    return out.sort((a, b) => b.s - a.s || a.p.p - b.p.p).slice(0, limit || 24);
  }

  /* ------------------------------------------------------------------ ranking */
  function search(rawQuery) {
    const q = parse(rawQuery);
    const n = INDEX.p.length;
    const score = new Float32Array(n);
    const hit = new Uint8Array(n);   // how many typed words this product answers
    const fuzzy = [];                // typed word → the catalogue word it was read as
    const read = [];                 // the typed words as they were read ("peper" → "pepper")
    let unknown = 0;                 // typed words the catalogue has no word for ("campfire", "laundry")
    const matched = Array.from({ length: n }, () => null); // per product: the catalogue words that matched (for highlighting)
    let reference = null;

    // typed words against every word in the catalogue
    const typing = /\S$/.test(rawQuery);
    const soft = new Set();
    q.terms.forEach((term, ti) => {
      const per = new Float32Array(n);
      const isLast = typing && ti === q.terms.length - 1;
      let bestWord = '', bestQ = 0, bestCount = 0;
      for (const [word, list] of VOCAB) {
        const ql = quality(term, word, isLast);
        if (!ql) continue;
        if (ql > bestQ || (ql === bestQ && list.length > bestCount)) { bestQ = ql; bestWord = word; bestCount = list.length; }
        for (const [pi, weight] of list) {
          const v = ql * weight;
          if (v > per[pi]) {
            per[pi] = v;
            (matched[pi] || (matched[pi] = new Set())).add(word);
          }
        }
      }
      // "campfire", "gym": understood as a mood or a time of day, though no bottle carries the word
      if (bestQ < 0.55 && (CONCEPTS[term] || DAY.has(term) || NIGHT.has(term))) { soft.add(ti); return; }
      // a mood next to other words ("dark and sexy", "warm vanilla") leans the ranking: bottles are not
      // required to have the word itself in their name or notes. Typed alone it is still looked up as a word.
      if (CONCEPTS[term] && q.terms.length > 1) {
        soft.add(ti);
        for (let i = 0; i < n; i++) if (per[i]) score[i] += per[i] * 0.35;
        return;
      }
      if (bestWord && bestQ < 0.8 && bestQ >= 0.55) fuzzy.push([term, bestWord]);
      if (bestQ < 0.55) unknown++;
      read.push(bestWord && bestQ >= 0.55 && bestQ < 1 && !isLast ? bestWord : bestWord && bestQ >= 0.55 && bestQ < 0.8 ? bestWord : term);
      for (let i = 0; i < n; i++) if (per[i]) { score[i] += per[i]; hit[i]++; }
    });
    const terms = q.terms.filter((t, ti) => !soft.has(ti));

    // moods: "sweet", "fresh", "smoky" …
    for (const c of q.concepts) {
      INDEX.p.forEach((p, i) => {
        let s = 0;
        (c.acc || []).forEach((a, k) => { if (p.accMap.has(a)) s = Math.max(s, (p.accMap.get(a) / 100) * (k === 0 ? 1 : k < 3 ? 0.75 : 0.55)); });
        let ns = 0;
        for (const nn of c.notes || []) if (p.nnotes.some((x) => x === nn || x.includes(nn))) ns++;
        const v = s * 4 + Math.min(ns, 3) * 0.9;
        if (v > 0) { score[i] += v; }
      });
    }

    // occasions: the bottles tagged for it, ordered by how well the mood of the occasion suits them
    for (const o of q.occasions) {
      let c = o.mood ? CONCEPTS[o.mood] : null;
      if (c && c.same) c = CONCEPTS[c.same];
      INDEX.p.forEach((p, i) => {
        if (!(p.occ || []).includes(o.tag)) return;
        let v = 2.2;
        if (c) {
          let a = 0;
          (c.acc || []).forEach((x, k) => { if (p.accMap.has(x)) a = Math.max(a, (p.accMap.get(x) / 100) * (k === 0 ? 1 : 0.75)); });
          v += a * 2.5;
        }
        score[i] += v;
      });
    }

    // "like Il Padrino": what we carry that shares its notes
    if (q.like && INDEX) {
      reference = findReference(q.like);
      if (reference) INDEX.p.forEach((p, i) => { const s = similarity(reference, p); if (s > 0.25) { score[i] += s * 6; hit[i] = Math.max(hit[i], 1); } });
    }

    const needTerms = terms.length;
    const hasIntent = needTerms || q.concepts.length || q.occasions.length || reference;
    const hasFilter = q.gender || q.max || q.min || q.season >= 0 || q.night || q.day || q.lasting || q.newest || q.inStock;
    const out = [];
    const typed = q.terms.join(' ');
    const phrase = terms.length ? terms.join(' ') : typed;
    const readPhrase = terms.length ? read.join(' ') : typed;
    INDEX.p.forEach((p, i) => {
      if (!within(q, p)) return;
      let s = score[i];
      if (needTerms) {
        if (!hit[i]) { if (!(q.concepts.length && s > 0) && !reference) return; }
        // a product that answers every word beats one that answers some of them
        const part = needTerms ? hit[i] / needTerms : 1;
        s *= part * part;
        if (phrase.length >= 3) {
          if (p.nn === phrase) s += 14;
          else if (p.nn.startsWith(phrase)) s += 9;
          else if ((p.nb + ' ' + p.nn).includes(phrase)) s += 6;
          else if (p.nb === phrase || p.nb.startsWith(phrase)) s += 4;
        }
        // the words together name one note, accord or perfumer of this bottle ("pink pepper", "christian provenzano")
        if (needTerms > 1 && hit[i] === needTerms) {
          if (p.nnotes.includes(readPhrase)) s += 7;
          else if (p.accMap.has(readPhrase)) s += 5 * (p.accMap.get(readPhrase) / 100) + 2;
          else if (norm(p.perf).includes(readPhrase)) s += 6;
          else if (norm(p.fam) === readPhrase) s += 5;
        }
      } else if (hasIntent) {
        if (s <= 0) return;
        // every word was a mood, yet together they are this bottle's name
        if (typed.length >= 3 && (p.nn === typed || p.nn.startsWith(typed + ' '))) s += 9;
      } else if (!hasFilter) return;
      else s = 1;
      // "not sweet", "no vanilla"
      for (const w of q.not) {
        let c = CONCEPTS[w];
        if (c && c.same) c = CONCEPTS[c.same];
        const accs = c ? c.acc : [w];
        let pen = 0;
        for (const a of accs) if (p.accMap.has(a)) pen = Math.max(pen, p.accMap.get(a));
        if (p.nnotes.some((x) => x === w || x.split(' ').includes(w))) pen = Math.max(pen, 70);
        if (pen >= 55) return;
        if (pen) s *= 1 - pen / 110;
      }
      if (q.season >= 0) s *= 0.45 + 0.55 * ((p.s[q.season] || 40) / 100);
      if (q.night) s *= 0.6 + 0.4 * ((p.dn[1] || 50) / 100);
      if (q.day) s *= 0.6 + 0.4 * ((p.dn[0] || 50) / 100);
      if (q.gender && p.g === q.gender) s *= 1.15;
      if (q.lasting) s *= /long|very|eternal|strong/i.test(p.lon + ' ' + p.sil) ? 1.25 : 0.9;
      if (!p.av) s *= 0.55;
      if (p.side && !terms.some((t) => p.side.includes(t))) s *= 0.3;
      if (s > 0) out.push([s, i]);
    });
    if (out.length > 1 && hasIntent && !q.newest) {
      let best = 0;
      for (const [s] of out) if (s > best) best = s;
      const floor = needTerms || reference ? Math.max(best * 0.12, needTerms ? 1.2 : 0.4) : Math.max(best * 0.5, 0.4);
      for (let k = out.length - 1; k >= 0; k--) if (out[k][0] < floor) out.splice(k, 1);
    }
    const wantsSide = terms.some((t) => INDEX.p.some((p) => p.side && p.side.includes(t)));
    if (q.newest) out.sort((a, b) => (INDEX.p[b[1]].c > INDEX.p[a[1]].c ? 1 : INDEX.p[b[1]].c < INDEX.p[a[1]].c ? -1 : b[0] - a[0]));
    else out.sort((a, b) => b[0] - a[0] || INDEX.p[a[1]].p - INDEX.p[b[1]].p);
    // candles, creams and sets come after the perfumes, unless one was asked for
    if (!wantsSide) { const perfumes = out.filter((x) => !INDEX.p[x[1]].side); if (perfumes.length) out.splice(0, out.length, ...perfumes, ...out.filter((x) => INDEX.p[x[1]].side)); }

    // houses, notes and perfumers the words point at
    const ents = [];
    if (phrase.length >= 2) {
      for (const e of ENTITIES) {
        let s = 0;
        if (e.n === phrase || e.n === readPhrase) s = 3;
        else if (e.n.startsWith(phrase) || e.n.startsWith(readPhrase)) s = 2;
        else if ((' ' + e.n).includes(' ' + phrase)) s = 1.5;
        else if (q.terms.length && q.terms.every((t) => e.n.split(' ').some((w) => quality(t, w, typing) >= 0.6))) s = 1;
        else if (q.terms.length > 1) { for (const t of read) if (t.length >= 3 && e.n === t) s = Math.max(s, 0.9); }
        if (s) ents.push([s + Math.min(e.count, 60) / 600 + (e.kind === 'brand' ? 0.3 : 0), e]);
      }
      ents.sort((a, b) => b[0] - a[0]);
    }
    return {
      q,
      reference,
      total: out.length,
      top: out.length ? out[0][0] : 0,
      products: out.slice(0, 8).map(([s, i]) => ({ p: INDEX.p[i], s, words: matched[i] })),
      list: (n) => out.slice(0, n || out.length).map(([s, i]) => ({ p: INDEX.p[i], s, words: matched[i] })),
      readAs: read.join(' '),
      entities: ents.slice(0, 7).map((x) => x[1]),
      fuzzy,
      unknown,
      hasIntent,
      hasFilter,
    };
  }

  /* ------------------------------------------------------------------ the advisor (AI) */
  const aiCache = new Map();
  let aiAbort = null, aiTimer = 0, aiFor = '';

  /** Is this a request the index cannot settle on its own? */
  function wantsAdvisor(res, raw) {
    if (!CFG.ai) return false;
    const text = raw.trim();
    const words = text.split(/\s+/).length;
    if (text.length < 4) return false;
    if (res.q.like && !res.reference) return true;   // "like Baccarat Rouge": a perfume we do not carry
    if (res.total === 0) return true;                // nothing matched the words
    if (words >= 2 && res.unknown > 0) return true;  // a request with a word the catalogue has no word for ("clean laundry", "for a wedding")
    if (words >= 2 && res.total < 3) return true;
    return false;                                    // every word was understood here: no need to wait for anyone
  }

  function askAdvisor(raw, res) {
    clearTimeout(aiTimer);
    if (aiAbort) aiAbort.abort();
    const key = norm(raw);
    aiFor = key;
    if (!wantsAdvisor(res, raw)) return renderAdvisor(null);
    if (aiCache.has(key)) return renderAdvisor(aiCache.get(key), raw);
    renderAdvisor('wait', raw);
    aiTimer = setTimeout(() => {
      aiAbort = new AbortController();
      const timeout = setTimeout(() => aiAbort && aiAbort.abort(), 9000);
      fetch(CFG.ai, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ q: raw.slice(0, 200) }), signal: aiAbort.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('advisor ' + r.status))))
        .then((d) => {
          clearTimeout(timeout);
          const picks = (d.picks || []).map((x) => ({ p: INDEX.p.find((p) => p.h === x.h), why: x.why || '' })).filter((x) => x.p);
          const answer = picks.length ? { say: d.say || '', picks } : null;
          aiCache.set(key, answer);
          if (aiFor === key) renderAdvisor(answer, raw);
        })
        .catch(() => {
          clearTimeout(timeout);
          if (aiFor === key) renderAdvisor(null);
        });
    }, 650);
  }

  /* ------------------------------------------------------------------ drawing */
  const img = (p, size) => (p.i ? `<img src="${esc(p.i)}?width=${size * 2}" alt="" decoding="async" width="${size}" height="${size}" data-rdls-h="${esc(p.h)}">` : '<span class="rdls-noimg" aria-hidden="true"></span>');

  function mark(text, words) {
    if (!words || !words.size) return esc(text);
    return esc(text).replace(/[A-Za-zÀ-ÿ0-9]+/g, (w) => (words.has(norm(w)) ? `<mark>${w}</mark>` : w));
  }

  function notesLine(p, words, concepts) {
    const want = new Set();
    for (const c of concepts || []) for (const n of c.notes || []) want.add(n);
    const seen = new Set();
    const scored = [];
    p.notes.forEach((n, k) => {
      const nn = p.nnotes[k];
      if (seen.has(nn)) return;
      seen.add(nn);
      const direct = words && nn.split(' ').some((w) => words.has(w));
      const mood = [...want].some((w) => nn === w || nn.includes(w));
      scored.push({ n, v: direct ? 2 : mood ? 1 : 0, k });
    });
    scored.sort((a, b) => b.v - a.v || a.k - b.k);
    const pick = scored.slice(0, 4);
    if (!pick.length) return p.acc.slice(0, 3).map(([n]) => esc(n)).join(' · ');
    return pick.map((x) => (x.v ? `<mark>${esc(x.n)}</mark>` : esc(x.n))).join(' · ');
  }

  function card(p, opts = {}) {
    const name = p.t.toLowerCase().startsWith(p.b.toLowerCase() + ' ') ? p.t.slice(p.b.length + 1) : p.t;
    return (
      `<a class="rdls-card${opts.ai ? ' rdls-card--ai' : ''}" href="/products/${esc(p.h)}" data-opt>` +
      `<span class="rdls-img">${img(p, 84)}</span>` +
      `<span class="rdls-info">` +
      `<span class="rdls-brand">${mark(p.b, opts.words)}</span>` +
      `<span class="rdls-name">${mark(name, opts.words)}</span>` +
      (opts.why ? `<span class="rdls-why">${esc(opts.why)}</span>` : `<span class="rdls-notes">${notesLine(p, opts.words, opts.concepts)}</span>`) +
      `<span class="rdls-price">${p.bo ? '<span class="rdls-out rdls-bo">Boutique only</span>' : `${p.pv ? 'From ' : ''}${money(p.p)}${p.cp ? ` <s>${money(p.cp)}</s>` : ''}${p.av ? '' : ' <span class="rdls-out">Sold out</span>'}`}</span>` +
      `</span></a>`
    );
  }

  const SPARK = '<svg class="rdls-spark" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M12 2l1.9 5.6a3 3 0 0 0 1.9 1.9L21.4 11.4a.6.6 0 0 1 0 1.2l-5.6 1.9a3 3 0 0 0-1.9 1.9L12 22l-1.9-5.6a3 3 0 0 0-1.9-1.9L2.6 12.6a.6.6 0 0 1 0-1.2l5.6-1.9a3 3 0 0 0 1.9-1.9z"/></svg>';

  function renderAdvisor(answer, raw) {
    const box = $('.rdls-ai');
    if (!box) return;
    if (!answer) { box.hidden = true; box.innerHTML = ''; return; }
    box.hidden = false;
    if (answer === 'wait') {
      box.innerHTML = `<div class="rdls-ai-head">${SPARK}<span>Redolia AI is reading your request…</span></div><div class="rdls-ai-skel"><i></i><i></i><i></i></div>`;
      return;
    }
    box.innerHTML =
      `<div class="rdls-ai-head">${SPARK}<span>Redolia AI</span></div>` +
      (answer.say ? `<p class="rdls-ai-say">${esc(answer.say)}</p>` : '') +
      `<div class="rdls-grid rdls-grid--ai">${answer.picks.slice(0, 4).map((x) => card(x.p, { ai: true, why: x.why })).join('')}</div>`;
    const none = $('.rdls-none');
    if (none) none.hidden = true; // the advisor answered what the words alone could not
    announce(`Redolia AI suggests ${plural(answer.picks.length, 'perfume')}.`);
  }

  function announce(text) {
    if (elStatus) elStatus.textContent = text;
  }

  const recentKey = 'rdl-search-recent';
  const recent = () => {
    try { return JSON.parse(localStorage.getItem(recentKey) || '[]').filter((x) => typeof x === 'string').slice(0, 5); } catch (e) { return []; }
  };
  const remember = (q) => {
    q = q.trim();
    if (q.length < 2) return;
    try { localStorage.setItem(recentKey, JSON.stringify([q, ...recent().filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 5))); } catch (e) { /* private window */ }
  };

  const chip = (label, query) => `<button type="button" class="rdls-chip" data-q="${esc(query || label)}">${esc(label)}</button>`;

  function renderHome() {
    elUnderstood.innerHTML = '';
    const rec = recent();
    elSide.innerHTML =
      (rec.length ? `<div class="rdls-h">Your recent searches</div><div class="rdls-chips">${rec.map((r) => chip(r)).join('')}</div>` : '') +
      `<div class="rdls-h">Popular</div><div class="rdls-chips">${CFG.popular.map((p) => chip(p)).join('')}</div>` +
      `<div class="rdls-h">Or just ask</div><div class="rdls-asks">${CFG.ask.map((a) => `<button type="button" class="rdls-ask" data-q="${esc(a)}">${SPARK}<span>${esc(a)}</span></button>`).join('')}</div>`;
    if (!INDEX) { elMain.innerHTML = '<div class="rdls-loading" aria-hidden="true"><i></i><i></i><i></i><i></i></div>'; return; }
    const fresh = [];
    const houseSeen = new Set();
    for (const p of INDEX.p.filter((x) => x.av && x.i && x.p >= 60).sort((a, b) => (a.c < b.c ? 1 : -1))) {
      if (houseSeen.has(p.b)) continue;
      houseSeen.add(p.b);
      fresh.push(p);
      if (fresh.length === 6) break;
    }
    const houses = (INDEX.brands || []).filter((b) => b[1]).slice(0, 10);
    elMain.innerHTML =
      `<div class="rdls-ai" hidden></div>` +
      `<div class="rdls-h">New in</div><div class="rdls-grid">${fresh.map((p) => card(p)).join('')}</div>` +
      (houses.length ? `<div class="rdls-h rdls-h--gap">Houses</div><div class="rdls-chips rdls-chips--links">${houses.map((b) => `<a class="rdls-chip" href="/collections/${esc(b[1])}" data-opt>${esc(b[0])}</a>`).join('')}</div>` : '');
  }

  function render(raw) {
    const res = search(raw);
    const q = res.q;
    // what we understood
    elUnderstood.innerHTML = q.chips.length
      ? `<span class="rdls-u-label">Understood</span>${q.chips.map((c) => `<span class="rdls-u">${esc(c)}</span>`).join('')}`
      : '';
    // left: pages the words point at
    const all = `${CFG.searchUrl}?q=${encodeURIComponent(raw.trim())}`;
    elSide.innerHTML =
      (res.entities.length
        ? `<div class="rdls-h">Go straight to</div><div class="rdls-ents">${res.entities
            .map((e) => (e.handle
              ? `<a class="rdls-ent" href="/collections/${esc(e.handle)}" data-opt><span class="rdls-ent-k">${e.label}</span><span class="rdls-ent-n">${esc(e.name)}</span><span class="rdls-ent-c">${e.count}</span></a>`
              : `<button type="button" class="rdls-ent" data-q="${esc(e.name)}" data-opt><span class="rdls-ent-k">${e.label}</span><span class="rdls-ent-n">${esc(e.name)}</span><span class="rdls-ent-c">${e.count}</span></button>`))
            .join('')}</div>`
        : '') +
      `<div class="rdls-h${res.entities.length ? ' rdls-h--gap' : ''}">Refine</div><div class="rdls-chips">${refinements(q).map((r) => chip(r.label, r.query)).join('')}</div>`;
    // right: products
    const corrected = res.fuzzy.length && res.total ? `<p class="rdls-meant">Showing results for <strong>${esc(res.fuzzy.map((f) => f[1]).join(' '))}</strong></p>` : '';
    const ref = res.reference ? `<p class="rdls-meant">Closest to <a href="/products/${esc(res.reference.h)}" data-opt><strong>${esc(res.reference.t)}</strong></a> by shared notes</p>` : '';
    let body;
    if (res.total) {
      body =
        `<div class="rdls-h">${res.total > 8 ? `Top matches <span class="rdls-count">of ${res.total}</span>` : plural(res.total, 'match', 'matches')}</div>` +
        corrected + ref +
        `<div class="rdls-grid" data-list>${res.products.map((x) => card(x.p, { words: x.words, concepts: q.concepts })).join('')}</div>` +
        `<a class="rdls-all" href="${esc(all)}" data-opt>${res.total > 8 ? `See all ${res.total} results` : 'Open the results page'} <span aria-hidden="true">→</span></a>`;
    } else {
      body =
        `<div class="rdls-none"><p class="rdls-none-t">No perfume matches “${esc(raw.trim())}” by name or notes.</p>` +
        `<p class="rdls-none-s">${CFG.ai ? 'Our AI is reading what you meant — or try a note, a house, or a price: ' : 'Try a note, a house, or a price: '}` +
        `${['vanilla', 'oud', 'under $200', 'for her'].map((x) => chip(x)).join(' ')}</p></div>`;
    }
    elMain.innerHTML = `<div class="rdls-ai" hidden></div>` + body;
    announce(res.total ? `${plural(res.total, 'match', 'matches')} for ${raw.trim()}.` : `No matches for ${raw.trim()}.`);
    askAdvisor(raw, res);
  }

  /** Ways to narrow the request that it does not already say. */
  function refinements(q) {
    const base = q.raw.trim();
    const out = [];
    const add = (label, extra) => out.push({ label, query: `${base} ${extra}`.trim() });
    if (!q.gender) { add('For him', 'for him'); add('For her', 'for her'); }
    if (!q.max && !q.min) { add('Under $150', 'under $150'); add('Under $250', 'under $250'); }
    if (q.season < 0) { add('For summer', 'for summer'); add('For winter', 'for winter'); }
    if (!q.night && !q.day) add('Date night', 'for date night');
    if (!q.lasting) add('Long lasting', 'long lasting');
    return out.slice(0, 6);
  }

  /* ------------------------------------------------------------------ behaviour */
  let lastTrigger = null, paintTimer = 0;
  // results first, then the shortcuts beside them
  const options = () => [...elMain.querySelectorAll('[data-opt], [data-q]'), ...elSide.querySelectorAll('[data-opt], [data-q]')].filter((el) => el.offsetParent !== null);

  /** Arrow keys walk from the field through every suggestion and result, and back. */
  function move(delta) {
    const els = [input, ...options()];
    let i = els.indexOf(document.activeElement);
    if (i < 0) i = 0;
    const next = els[(i + delta + els.length) % els.length];
    next.focus();
    if (next !== input) next.scrollIntoView({ block: 'nearest' });
  }

  function update() {
    const raw = input.value;
    elClear.hidden = !raw;
    if (!raw.trim()) { clearTimeout(aiTimer); if (aiAbort) aiAbort.abort(); aiFor = ''; return renderHome(); }
    if (!INDEX) return; // drawn as soon as the index arrives
    render(raw);
  }

  function setQuery(q) {
    input.value = q;
    input.focus();
    update();
  }

  function open(trigger) {
    refreshIfStale();
    lastTrigger = trigger || document.activeElement;
    input.placeholder = window.innerWidth < 820 ? 'Search perfumes, notes, houses…' : 'Search a perfume, a house, a note — or describe what you want';
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('rdls-open');
    update();
    requestAnimationFrame(() => { input.focus(); input.select(); });
    load().then(update).catch(() => {
      elMain.innerHTML = `<div class="rdls-none"><p class="rdls-none-t">Search is taking a moment.</p><p class="rdls-none-s">Press Enter to see every result for what you typed.</p></div>`;
    });
    if (CFG.ai && !open.warmed) { open.warmed = true; fetch(CFG.ai, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ warm: 1 }) }).catch(() => {}); }
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('rdls-open');
    if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
  });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) return close(); // the dimmed area around the panel
    const q = e.target.closest('[data-q]');
    if (q) { e.preventDefault(); return setQuery(q.getAttribute('data-q')); }
    if (e.target.closest('.rdls-close')) return close();
    if (e.target.closest('.rdls-clear')) return setQuery('');
    const link = e.target.closest('a[href]');
    if (link && input.value.trim()) remember(input.value);
  });
  input.addEventListener('input', () => {
    clearTimeout(paintTimer);
    paintTimer = setTimeout(update, 40);
  });
  dialog.addEventListener('keydown', (e) => {
    const onField = document.activeElement === input;
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Enter' && onField) {
      // Enter goes to the results page (the form's own action), which ranks the same way.
      if (!input.value.trim()) e.preventDefault();
      else remember(input.value);
    }
    // Typing while a result has the focus goes on in the field.
    else if (!onField && !e.metaKey && !e.ctrlKey && !e.altKey && ((e.key.length === 1 && e.key !== ' ') || e.key === 'Backspace')) input.focus();
  });

  // Take over the header's search button, and open with "/" or ⌘K / Ctrl+K.
  window.addEventListener(
    'click',
    (e) => {
      const t = e.target instanceof Element ? e.target.closest('search-button button, [on\\:click*="search-modal/showDialog"], [data-rdl-search-open]') : null;
      if (!t || dialog.contains(t)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open(t);
    },
    true
  );
  window.addEventListener('keydown', (e) => {
    const typing = /^(input|textarea|select)$/i.test((e.target && e.target.tagName) || '') || (e.target && e.target.isContentEditable);
    if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); dialog.open ? close() : open(); }
    else if (e.key === '/' && !typing && !dialog.open) { e.preventDefault(); open(); }
  });
  // Have the index ready before the first click when the browser is idle or the pointer nears the button.
  const warm = () => load().catch(() => {});
  document.addEventListener('pointerover', function near(e) {
    if (e.target instanceof Element && e.target.closest('search-button, [data-rdl-search-open]')) { warm(); document.removeEventListener('pointerover', near); }
  });
  const saver = navigator.connection && navigator.connection.saveData;
  if (!saver) {
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 6000 });
    else setTimeout(warm, 3500);
  }

  window.RDLSearch = {
    open, close, parse, load,
    search: (q) => load().then(() => search(q)),
    searchNow: search,                      // once load() has resolved
    wants: wantsAdvisor,
    similar: similarTo,                     // bottles closest to a few given ones
    index: () => INDEX,
    cfg: CFG,
    remember,
    util: { esc, money, norm, mark, notesLine, plural, SPARK },
  };
  dialog.setAttribute('data-ready', '1');
  document.dispatchEvent(new CustomEvent('rdl-search-ready'));
})();

/* Picture safety net (2026-10-11, after 28 products showed a broken picture in search): when a product picture fails to
   load, ask the shop for that product's current picture; if that fails too, show a plain tile, never a broken icon.
   Works for the panel and for the results page (any <img data-rdls-h="handle">). */
(function () {
  function tile(im) {
    var s = document.createElement('span');
    s.className = 'rdls-noimg';
    s.setAttribute('aria-hidden', 'true');
    s.style.cssText = 'display:block;width:' + (im.getAttribute('width') || 90) + 'px;height:' + (im.getAttribute('height') || 90) + 'px;max-width:100%;background:#F1EBDD;border-radius:6px';
    if (im.parentNode) im.parentNode.replaceChild(s, im);
  }
  document.addEventListener('error', function (e) {
    var im = e.target;
    if (!im || im.tagName !== 'IMG' || !im.dataset || !im.dataset.rdlsH) return;
    if (im.dataset.rdlsTried) { tile(im); return; }
    im.dataset.rdlsTried = '1';
    fetch('/products/' + encodeURIComponent(im.dataset.rdlsH) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var u = p && (p.featured_image || (p.images && p.images[0]));
        if (!u) { tile(im); return; }
        u = String(u).split('?')[0]; if (u.indexOf('//') === 0) u = 'https:' + u;
        im.src = u + '?width=' + (parseInt(im.getAttribute('width') || '90', 10) * 2);
      })
      .catch(function () { tile(im); });
  }, true);
})();
